import { useMemo } from "react";
import { create } from "zustand";

import {
  CARD_DEFAULT_H,
  CARD_DEFAULT_W,
  CARD_MIN_H,
  CARD_MIN_W,
  clampPos,
  clampSize,
  findFreeSpot,
  type Box,
} from "@/warRoom/canvas/layout";
import { clearCache } from "@/warRoom/datasets/cache";
import { DATASETS, datasetByKey } from "@/warRoom/datasets/registry";
import { tr } from "@/warRoom/lib/i18n";
import { canList, parseRoles } from "@/warRoom/lib/permissions";
import { authClient } from "@/lib/auth-clients";
import type { Dataset, Row } from "@/warRoom/datasets/types";
import {
  aggregationsFor,
  datasetSupportsForm,
  formByType,
  formsForDataset,
  measureFieldsFor,
  seriesFieldsFor,
  suggestForm,
  xCandidatesFor,
  xFieldsFor,
  yCandidatesFor,
} from "@/warRoom/graph/chartForms";
import {
  emptySpec,
  type Aggregation,
  type ChartType,
  type GraphSpec,
  type SortBy,
  type TimeGrain,
} from "@/warRoom/graph/spec";
import type { Mode } from "@/warRoom/theme/palette";

import {
  canvasFingerprint,
  currentCompanyId,
  readCanvas,
  toPayload,
  writeCanvas,
  type CanvasPayload,
  type SavedCanvas,
} from "./persist";
import { fetchRemoteCanvas, pushRemoteCanvas } from "./remote";

export type ViewMode = "chart" | "table";

/**
 * 畫布上的一張圖 = 一份自己的 spec（資料、圖型、X/Y、篩選）+ 一塊自己的位置與大小。
 * 篩選也在 spec 裡 —— 畫布上每張圖各自成立，共用一條篩選列會騙人。
 */
export type Widget = Box & {
  id: string;
  spec: GraphSpec;
  viewMode: ViewMode;
};

type State = {
  /** 畫布上的圖，陣列順序 = 疊放順序（後面的在上面） */
  widgets: Widget[];
  /** 目前選中的那張圖：工具箱第 ③ 步編輯的就是它 */
  selectedId: string | null;
  /** 第 ① 步選的資料 —— 接下來拖進畫布的新圖用它 */
  activeDatasetKey: string | null;

  /** 抓回來的資料按資料集分開放，一份資料多張圖共用同一份 rows */
  rowsByDataset: Record<string, Row[]>;
  loadingByDataset: Record<string, boolean>;
  errorByDataset: Record<string, string>;
  /** 操作層的錯誤（例如這份資料畫不出這種圖），不是某個資料集抓不到 */
  error: string | null;

  mode: Mode;
  /** 畫布目前的可用寬度：決定「點一下放進畫布」要排幾欄 */
  canvasWidth: number;

  /**
   * 目前畫布屬於哪一間公司 —— 也是存檔的 key。
   * null＝這個 session 還沒還原過任何畫布。
   */
  companyId: string | null;
  /** 從 localStorage 還原畫布，回傳還原了幾張圖 */
  hydrate: (companyId?: string) => number;
  syncRemote: () => Promise<number | null>;

  /** 這個帳號在目前公司的角色。null＝還沒問到，資料集一律照舊全開 */
  roles: string[] | null;
  setRoles: (roles: string[] | null) => void;
  loadRoles: () => Promise<void>;

  setCanvasWidth: (w: number) => void;
  pickDataset: (key: string) => void;
  addWidget: (
    type: ChartType,
    opts?: {
      x?: number;
      y?: number;
      datasetKey?: string;
      canvasWidth?: number;
    },
  ) => string | null;
  /** 第 ② 步點一下：不用拖，直接放到畫布上的空位 */
  pickChart: (type: ChartType) => string | null;

  selectWidget: (id: string | null) => void;
  moveWidget: (id: string, x: number, y: number, exact?: boolean) => void;
  resizeWidget: (id: string, w: number, h: number, exact?: boolean) => void;
  removeWidget: (id: string) => void;
  duplicateWidget: (id: string) => string | null;
  clearCanvas: () => void;
  setWidgetViewMode: (id: string, v: ViewMode) => void;
  setWidgetDataset: (id: string, key: string) => void;
  setWidgetChartType: (id: string, type: ChartType) => void;

  /* 以下都作用在「選中的那張圖」—— 工具箱第 ③ 步的每個控制項 */
  setXField: (key: string) => void;
  setYField: (key: string | null) => void;
  setAggregation: (agg: Aggregation) => void;
  setSeriesField: (key: string | null) => void;
  setTimeGrain: (g: TimeGrain) => void;
  setTopN: (n: number) => void;
  setSortBy: (s: SortBy) => void;
  setDateField: (key: string | null) => void;
  setDateRange: (range: [string, string] | null) => void;
  setDimFilter: (key: string, values: string[]) => void;
  clearDimFilters: () => void;

  setMode: (m: Mode) => void;
  reload: () => Promise<void>;
  reset: () => void;
};

/**
 * 選完資料集或圖型之後，把 X／Y／數列補成一組「有效」的預設值。
 * 選資料集就會順便補出圖型，所以拖進畫布的圖一定畫得出東西。
 */
export function reconcile(spec: GraphSpec): GraphSpec {
  const dataset = spec.datasetKey ? datasetByKey(spec.datasetKey) : undefined;
  if (!dataset) return spec;

  let next = { ...spec };

  // 圖型必須是這份資料畫得出來的
  const available = formsForDataset(dataset);
  if (!next.chartType || !available.some((f) => f.type === next.chartType)) {
    next.chartType = (available[0] ?? suggestForm(dataset)).type;
  }
  const form = formByType(next.chartType);

  // X 軸
  const xs = xFieldsFor(dataset, form);
  const measures = measureFieldsFor(dataset);
  if (form.type === "scatter") {
    if (!next.xField || !measures.some((m) => m.key === next.xField)) {
      next.xField = measures[0]?.key ?? null;
    }
    // 散佈圖 X／Y 必須是兩個不同的量值
    const yCandidates = yCandidatesFor(dataset, form, next.xField);
    if (
      !next.yField ||
      next.yField === next.xField ||
      !yCandidates.some((m) => m.key === next.yField)
    ) {
      next.yField = yCandidates[0]?.key ?? null;
    }
    next.aggregation = "sum";
  } else {
    // 使用者選的軸只要還是這份資料裡的欄位就留著 —— xs（建議清單）只拿來挑預設值。
    // 這樣「X / Y 可以選全部欄位」才不會選完又被改回去。
    const xCandidates = xCandidatesFor(dataset, form);
    if (!next.xField || !xCandidates.some((f) => f.key === next.xField)) {
      // 有時間欄位優先當 X 軸，不然拿第一個維度
      const time = xs.find((f) => f.role === "time");
      next.xField = (time ?? xs[0] ?? xCandidates[0])?.key ?? null;
    }
    if (next.yField && !dataset.fields.some((f) => f.key === next.yField)) {
      next.yField = null;
    }

    // 聚合方式跟著 Y 欄位走：沒 Y 就是筆數，非量值只能算相異數
    const yMeta = next.yField
      ? dataset.fields.find((f) => f.key === next.yField)
      : undefined;
    const aggs = aggregationsFor(yMeta);
    if (!aggs.includes(next.aggregation)) next.aggregation = aggs[0];
  }

  // 數列
  const seriesOptions = seriesFieldsFor(dataset, next.xField);
  if (
    next.seriesField &&
    !seriesOptions.some((f) => f.key === next.seriesField)
  ) {
    next.seriesField = null;
  }
  if (!form.supportsSeries) {
    next.seriesField = null;
  } else if (form.requiresSeries && !next.seriesField) {
    next.seriesField = seriesOptions[0]?.key ?? null;
    // 這種圖非要數列不可，湊不出來就退回單一數列的版本
    if (!next.seriesField) {
      next.chartType = form.type === "stackedBar" ? "bar" : "column";
    }
  }

  // 維度篩選只留這份資料有的欄位
  const dimKeys = new Set(dataset.fields.map((f) => f.key));
  const keptFilters = Object.fromEntries(
    Object.entries(next.dimFilters).filter(
      ([k, v]) => dimKeys.has(k) && v.length > 0,
    ),
  );
  if (Object.keys(keptFilters).length !== Object.keys(next.dimFilters).length) {
    next.dimFilters = keptFilters;
  }

  // 日期篩選欄位
  const times = dataset.fields.filter((f) => f.role === "time");
  if (next.dateField && !times.some((f) => f.key === next.dateField)) {
    next.dateField = null;
    next.dateRange = null;
  }
  if (!next.dateField && times.length > 0) {
    const xIsTime = times.find((f) => f.key === next.xField);
    next.dateField = (xIsTime ?? times[0]).key;
  }

  return next;
}

let seq = 0;
const nextId = () => `w${(seq += 1)}`;

/**
 * 還原存檔之後把流水號推到已用過的最大值 ——
 * 不推的話還原了 w1..w5，下一張新圖又叫 w1，兩張圖共用一個 id。
 */
const primeIdSeq = (ids: string[]) => {
  for (const id of ids) {
    const n = Number(/^w(\d+)$/.exec(id)?.[1]);
    if (Number.isFinite(n) && n > seq) seq = n;
  }
};

/** 連續拖動、連打字時不要每次都寫 localStorage */
const SAVE_DEBOUNCE_MS = 400;

const REMOTE_SAVE_DEBOUNCE_MS = 1500;

let suppressSave = false;
let hydratedSavedAt = "";
let hydratedFingerprint = "";
let remoteSyncedFor: string | null = null;
let lastPushedFingerprint: string | null = null;

/** 沒量過畫布寬度時（例如測試、還沒 mount）用這個寬度排位置 */
const FALLBACK_CANVAS_WIDTH = 1080;

export const useGraphStore = create<State>((set, get) => {
  const load = async (dataset: Dataset) => {
    set((s) => ({
      loadingByDataset: { ...s.loadingByDataset, [dataset.key]: true },
      errorByDataset: Object.fromEntries(
        Object.entries(s.errorByDataset).filter(([k]) => k !== dataset.key),
      ),
    }));
    try {
      const rows = await dataset.fetch();
      set((s) => ({
        rowsByDataset: { ...s.rowsByDataset, [dataset.key]: rows },
        loadingByDataset: { ...s.loadingByDataset, [dataset.key]: false },
      }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set((s) => ({
        loadingByDataset: { ...s.loadingByDataset, [dataset.key]: false },
        errorByDataset: {
          ...s.errorByDataset,
          [dataset.key]:
            msg.includes("FORBIDDEN") || msg.includes("403")
              ? tr("warRoom.errors.forbidden", {
                  resource: dataset.resource,
                  message: msg,
                })
              : msg,
        },
      }));
    }
  };

  /**
   * 同一份資料只抓一次：第二張圖用同一個資料集時不會再打一次 API。
   * 抓失敗過的資料集不自動重試 —— 不然那張圖每調一次軸就再打一次會失敗的 API。
   * 重試走「使用者又選了它」或工具箱的「重新抓資料」。
   */
  const ensureRows = (key: string | null, retry = false) => {
    if (!key) return;
    const s = get();
    if (s.rowsByDataset[key] || s.loadingByDataset[key]) return;
    if (!retry && s.errorByDataset[key]) return;
    const dataset = datasetByKey(key);
    if (dataset) void load(dataset);
  };

  const applyCanvas = (companyId: string, saved: SavedCanvas | null) => {
    const widgets = (saved?.widgets ?? []).map((w) => ({
      ...w,
      // 存檔只保證形狀對；「這份資料畫不畫得出這種圖」在這裡才修
      spec: reconcile(w.spec),
    }));
    primeIdSeq(widgets.map((w) => w.id));

    clearCache();
    suppressSave = true;
    try {
      set({
        companyId,
        widgets,
        selectedId: null,
        activeDatasetKey: saved?.activeDatasetKey ?? null,
        // mode: saved?.mode ?? "light",
        mode: "light",
        rowsByDataset: {},
        loadingByDataset: {},
        errorByDataset: {},
        error: null,
      });
    } finally {
      suppressSave = false;
    }

    // rows 沒有存檔，還原出來的圖要自己去抓一次
    const keys = new Set(
      widgets
        .map((w) => w.spec.datasetKey)
        .filter((k): k is string => k !== null),
    );
    for (const key of keys) ensureRows(key);
    return widgets;
  };

  /** 改一張圖的 spec：一律經過 reconcile，改完的狀態一定畫得出來 */
  const patchSpec = (id: string | null, fn: (spec: GraphSpec) => GraphSpec) => {
    if (!id) return;
    const widget = get().widgets.find((w) => w.id === id);
    if (!widget) return;
    const next = reconcile(fn(widget.spec));
    set((s) => ({
      widgets: s.widgets.map((w) => (w.id === id ? { ...w, spec: next } : w)),
      error: null,
    }));
    ensureRows(next.datasetKey);
  };

  const patchSelected = (fn: (spec: GraphSpec) => GraphSpec) =>
    patchSpec(get().selectedId, fn);

  return {
    widgets: [],
    selectedId: null,
    activeDatasetKey: null,
    rowsByDataset: {},
    loadingByDataset: {},
    errorByDataset: {},
    error: null,
    mode: "light",
    canvasWidth: FALLBACK_CANVAS_WIDTH,
    companyId: null,
    roles: null,

    setCanvasWidth: (w) =>
      set({ canvasWidth: Math.max(CARD_DEFAULT_W, Math.round(w)) }),

    // 第 ① 步只決定「接下來的新圖用哪份資料」，不動畫布上已經有的圖 ——
    // 所以同一塊畫布可以同時放專案的圖和應收的圖。
    // 要換某張圖的資料，在第 ③ 步（那張圖自己的面板）換。
    pickDataset: (key) => {
      set({ activeDatasetKey: key, error: null });
      ensureRows(key, true);
    },

    addWidget: (type, opts) => {
      const datasetKey = opts?.datasetKey ?? get().activeDatasetKey;
      if (!datasetKey) {
        set({ error: tr("warRoom.errors.pickDatasetFirst") });
        return null;
      }
      const dataset = datasetByKey(datasetKey);
      if (!dataset) return null;

      const form = formByType(type);
      if (!datasetSupportsForm(dataset, form)) {
        set({
          error: tr("warRoom.errors.unsupportedChart", {
            dataset: dataset.label,
            chart: form.label,
          }),
        });
        return null;
      }

      const size = { w: CARD_DEFAULT_W, h: CARD_DEFAULT_H };
      const pos =
        opts?.x !== undefined && opts?.y !== undefined
          ? clampPos(opts.x, opts.y)
          : findFreeSpot(
              get().widgets,
              size,
              opts?.canvasWidth ?? get().canvasWidth,
            );

      const id = nextId();
      const widget: Widget = {
        id,
        spec: reconcile({ ...emptySpec, datasetKey, chartType: type }),
        viewMode: "chart",
        ...pos,
        ...size,
      };
      set((s) => ({
        widgets: [...s.widgets, widget],
        selectedId: id,
        error: null,
      }));
      ensureRows(datasetKey);
      return id;
    },

    pickChart: (type) => get().addWidget(type),

    // 選中一張圖也把第 ① 步的資料同步過去 —— 面板顯示的永遠是「這張圖在看的那份資料」
    selectWidget: (id) =>
      set((s) => {
        const widget = id ? s.widgets.find((w) => w.id === id) : undefined;
        return {
          selectedId: widget ? widget.id : null,
          activeDatasetKey: widget?.spec.datasetKey ?? s.activeDatasetKey,
        };
      }),

    moveWidget: (id, x, y, exact) =>
      set((s) => ({
        widgets: s.widgets.map((w) =>
          w.id === id
            ? {
                ...w,
                ...(exact
                  ? {
                      x: Math.max(0, Math.round(x)),
                      y: Math.max(0, Math.round(y)),
                    }
                  : clampPos(x, y)),
              }
            : w,
        ),
      })),

    resizeWidget: (id, w, h, exact) =>
      set((s) => ({
        widgets: s.widgets.map((x) =>
          x.id === id
            ? {
                ...x,
                ...(exact
                  ? {
                      w: Math.max(CARD_MIN_W, Math.round(w)),
                      h: Math.max(CARD_MIN_H, Math.round(h)),
                    }
                  : clampSize(w, h)),
              }
            : x,
        ),
      })),

    removeWidget: (id) =>
      set((s) => ({
        widgets: s.widgets.filter((w) => w.id !== id),
        selectedId: s.selectedId === id ? null : s.selectedId,
      })),

    duplicateWidget: (id) => {
      const source = get().widgets.find((w) => w.id === id);
      if (!source) return null;
      const copyId = nextId();
      const pos = findFreeSpot(
        get().widgets,
        { w: source.w, h: source.h },
        get().canvasWidth,
      );
      set((s) => ({
        widgets: [...s.widgets, { ...source, id: copyId, ...pos }],
        selectedId: copyId,
      }));
      return copyId;
    },

    clearCanvas: () => set({ widgets: [], selectedId: null, error: null }),

    setWidgetViewMode: (id, v) =>
      set((s) => ({
        widgets: s.widgets.map((w) =>
          w.id === id ? { ...w, viewMode: v } : w,
        ),
      })),

    setWidgetDataset: (id, key) => {
      const dataset = datasetByKey(key);
      if (!dataset) return;
      set({ activeDatasetKey: key });
      patchSpec(id, (spec) => ({ ...spec, datasetKey: key }));
      ensureRows(key, true);
    },

    setWidgetChartType: (id, type) => {
      const widget = get().widgets.find((w) => w.id === id);
      const dataset = widget?.spec.datasetKey
        ? datasetByKey(widget.spec.datasetKey)
        : undefined;
      if (!widget || !dataset) return;
      if (!datasetSupportsForm(dataset, formByType(type))) {
        set({
          error: tr("warRoom.errors.unsupportedChart", {
            dataset: dataset.label,
            chart: formByType(type).label,
          }),
        });
        return;
      }
      patchSpec(id, (spec) => ({ ...spec, chartType: type }));
    },

    setXField: (key) => patchSelected((spec) => ({ ...spec, xField: key })),

    setYField: (key) =>
      patchSelected((spec) => {
        const dataset = spec.datasetKey
          ? datasetByKey(spec.datasetKey)
          : undefined;
        const fieldAt = (k: string | null) =>
          k ? dataset?.fields.find((f) => f.key === k) : undefined;
        const nextField = fieldAt(key);
        const aggs = aggregationsFor(nextField);
        // 只有「量值換量值」才留著原本的聚合方式；
        // 其餘一律回到這個欄位算得出來的第一個（沒 Y → 筆數、文字／日期 → 相異數）
        const keepAgg =
          fieldAt(spec.yField)?.role === "measure" &&
          nextField?.role === "measure" &&
          aggs.includes(spec.aggregation);
        return {
          ...spec,
          yField: key,
          aggregation: keepAgg ? spec.aggregation : aggs[0],
        };
      }),

    // 經過 reconcile，選到這個 Y 欄位算不出來的聚合方式時會被拉回合法值
    setAggregation: (agg) =>
      patchSelected((spec) => ({ ...spec, aggregation: agg })),
    setSeriesField: (key) =>
      patchSelected((spec) => ({ ...spec, seriesField: key })),
    setTimeGrain: (g) => patchSelected((spec) => ({ ...spec, timeGrain: g })),
    setTopN: (n) => patchSelected((spec) => ({ ...spec, topN: n })),
    setSortBy: (s) => patchSelected((spec) => ({ ...spec, sortBy: s })),
    setDateField: (key) =>
      patchSelected((spec) => ({ ...spec, dateField: key })),
    setDateRange: (range) =>
      patchSelected((spec) => ({ ...spec, dateRange: range })),
    setDimFilter: (key, values) =>
      patchSelected((spec) => ({
        ...spec,
        dimFilters: { ...spec.dimFilters, [key]: values },
      })),
    clearDimFilters: () =>
      patchSelected((spec) => ({ ...spec, dimFilters: {} })),

    setMode: (m) => set({ mode: m }),

    /**
     * 還原這間公司的畫布。
     *
     * 只有「換了公司」才真的重跑 —— 同一間公司在頁面之間來回切，
     * 不該把已經抓好的 rows 丟掉重抓。
     * 換公司時 rows 一定要清空：那是上一間公司的資料。
     */
    hydrate: (sessionCompanyId) => {
      const legacyCompanyId = currentCompanyId();
      const companyId = sessionCompanyId ?? legacyCompanyId;
      if (get().companyId === companyId) {
        attachPersistence();
        return 0;
      }

      const saved =
        readCanvas(companyId) ??
        (companyId !== legacyCompanyId ? readCanvas(legacyCompanyId) : null);
      hydratedSavedAt = saved?.savedAt ?? "";
      remoteSyncedFor = null;
      lastPushedFingerprint = null;
      const widgets = applyCanvas(companyId, saved);
      hydratedFingerprint = canvasFingerprint(get());

      attachPersistence();
      return widgets.length;
    },

    syncRemote: async () => {
      const companyId = get().companyId;
      if (!companyId || remoteSyncedFor === companyId) return null;
      remoteSyncedFor = companyId;

      const remote = await fetchRemoteCanvas(companyId);
      if (remote === undefined) {
        if (remoteSyncedFor === companyId) remoteSyncedFor = null;
        return null;
      }
      const state = get();
      if (state.companyId !== companyId) return null;
      if (canvasFingerprint(state) !== hydratedFingerprint) return null;

      if (remote && remote.savedAt > hydratedSavedAt) {
        const widgets = applyCanvas(companyId, remote);
        writeCanvas({ ...remote, widgets }, companyId);
        hydratedSavedAt = remote.savedAt;
        hydratedFingerprint = canvasFingerprint(get());
        lastPushedFingerprint = hydratedFingerprint;
        return widgets.length;
      }

      if (hydratedSavedAt && (!remote || hydratedSavedAt > remote.savedAt)) {
        void pushPayload(
          toPayload({ ...state, savedAt: hydratedSavedAt }, companyId),
        );
      } else if (remote) {
        lastPushedFingerprint = canvasFingerprint(remote);
      }
      return null;
    },

    setRoles: (roles) => set({ roles }),

    // 問不到角色就維持 null（＝全開），不要把讀不到角色當成「沒有權限」
    loadRoles: async () => {
      try {
        const result = await authClient.organization.getActiveMember();
        const roles = parseRoles(result?.data?.role);
        if (roles.length > 0) set({ roles });
      } catch {
        /* 保持 null */
      }
    },

    // 重新抓：畫布上用到的每一份資料都要，不然只有選中的那張圖是新的
    reload: async () => {
      clearCache();
      const state = get();
      const keys = new Set(
        [
          ...state.widgets.map((w) => w.spec.datasetKey),
          state.activeDatasetKey,
        ].filter((k): k is string => !!k),
      );
      set({ rowsByDataset: {}, errorByDataset: {} });
      await Promise.all(
        [...keys].map((k) => {
          const dataset = datasetByKey(k);
          return dataset ? load(dataset) : Promise.resolve();
        }),
      );
    },

    reset: () => {
      clearCache();
      set({
        widgets: [],
        selectedId: null,
        activeDatasetKey: null,
        rowsByDataset: {},
        loadingByDataset: {},
        errorByDataset: {},
        error: null,
      });
    },
  };
});

/**
 * 畫布一變動就（延遲）寫回 localStorage。
 *
 * 一定要等 hydrate 之後才掛 —— 頁面剛進來時畫布是空的，
 * 先掛上去的話那個空狀態會把存檔洗掉。
 */
let saveTimer: ReturnType<typeof setTimeout> | undefined;
let persistAttached = false;
function attachPersistence() {
  if (persistAttached) return;
  persistAttached = true;
  useGraphStore.subscribe((s, prev) => {
    if (suppressSave) return;
    // rows／loading／錯誤訊息的變動不用存 —— 存的只有使用者排出來的東西
    if (
      s.widgets === prev.widgets &&
      s.mode === prev.mode &&
      s.activeDatasetKey === prev.activeDatasetKey
    ) {
      return;
    }
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      const now = useGraphStore.getState();
      if (!now.companyId) return;
      const payload = writeCanvas(
        {
          widgets: now.widgets,
          activeDatasetKey: now.activeDatasetKey,
          mode: now.mode,
        },
        now.companyId,
      );
      clearTimeout(remoteTimer);
      remoteTimer = setTimeout(
        () => void pushPayload(payload),
        REMOTE_SAVE_DEBOUNCE_MS,
      );
    }, SAVE_DEBOUNCE_MS);
  });
}

let remoteTimer: ReturnType<typeof setTimeout> | undefined;
async function pushPayload(payload: CanvasPayload) {
  const fingerprint = canvasFingerprint(payload);
  if (fingerprint === lastPushedFingerprint) return;
  if (await pushRemoteCanvas(payload)) lastPushedFingerprint = fingerprint;
}

/** 測試用：把訂閱狀態歸零，讓下一次 hydrate 重新掛一次 */
export function __resetPersistenceForTest() {
  clearTimeout(saveTimer);
  clearTimeout(remoteTimer);
  persistAttached = false;
  remoteSyncedFor = null;
  lastPushedFingerprint = null;
}

/** 選單看得到的資料。 */
export const allDatasets = DATASETS;

/**
 * 這個帳號讀得到的資料集。角色還沒問到時是全部 ——
 * 這裡擋掉的只是「一定會 403 的那幾份」，不是另一套權限。
 */
export const useVisibleDatasets = () => {
  const roles = useGraphStore((s) => s.roles);
  return useMemo(
    () => DATASETS.filter((d) => canList(d.resource, roles)),
    [roles],
  );
};

/** 目前選中的那張圖 —— 工具箱第 ③ 步整段吃這個 */
export const useSelectedWidget = () =>
  useGraphStore((s) => s.widgets.find((w) => w.id === s.selectedId) ?? null);

/** 有任何一份資料在抓 —— 工具箱的重新整理鈕吃這個 */
export const useAnyLoading = () =>
  useGraphStore((s) => Object.values(s.loadingByDataset).some(Boolean));
